import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  subscribeDownloadHistoryRefresh,
  useDownloadPolling,
} from '../contexts/DownloadPollingContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import Label from '../components/ui/Label';
import Input from '../components/ui/Input';
import DateField from '../components/ui/DateField';
import Button from '../components/ui/Button';
import { FunctionalSelect, SelectItem } from '../components/ui/Select';
import ErrorDialog from '../components/ui/ErrorDialog';
import Modal from '../components/ui/Modal';
import ListPagination, { LIST_PAGE_SIZE } from '../components/ui/ListPagination';
import { toast } from 'react-hot-toast';
import { HEADER_SELECT_CLASS, PageHeaderActions } from '../components/PageHeaderActions';
import { api } from '../services/authService';
import {
  DOWNLOAD_BTN_CLASS,
  buildFileDownloadHref,
  sortDownloadFileObjects,
} from '../services/fileDownload';
import { organizationsService } from '../services/organizationsService';
import { ChevronRight, ChevronDown } from 'lucide-react';

const HISTORY_FETCH_LIMIT = 200;

function formatSignalDownloadDateCell(value) {
  return value || '—';
}

function downloadHistoryStatusBadgeClass(processingStatus) {
  const s = (processingStatus || '').toLowerCase();
  if (s === 'complete' || s === 'completed') return 'bg-blue-100 text-blue-800';
  if (s === 'failed') return 'bg-red-100 text-red-800';
  if (s === 'pending') return 'bg-yellow-100 text-yellow-800';
  if (s === 'started' || s === 'processing') return 'bg-green-100 text-green-800';
  return 'bg-gray-100 text-gray-800';
}

function canDownloadSignalFromHistory(processingStatus) {
  const s = (processingStatus || '').toLowerCase();
  return s === 'complete' || s === 'completed';
}

const TARGET_FILE_SIZE_GB_OPTIONS = ['10', '50', '100', '500'];

/** Normalize FastAPI error detail (string, array of { msg }, or object) to a string for display. */
function formatApiErrorDetail(detail) {
  if (detail == null) return '';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && e.msg != null ? e.msg : JSON.stringify(e))).join('\n');
  }
  if (typeof detail === 'object') return JSON.stringify(detail);
  return String(detail);
}

function formatFileSize(bytes) {
  if (bytes == null || Number.isNaN(bytes)) return '—';
  const n = Number(bytes);
  if (n < 1024) return `${n} B`;
  const kb = n / 1024;
  if (kb < 1024) return `${kb < 10 ? kb.toFixed(1) : Math.round(kb)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
  const gb = mb / 1024;
  return `${gb < 10 ? gb.toFixed(2) : gb.toFixed(1)} GB`;
}

function s3ObjectFilename(key) {
  if (key == null || key === '') return '—';
  const i = key.lastIndexOf('/');
  return i >= 0 ? key.slice(i + 1) : key;
}

const Signals = () => {
  const { user: currentUser } = useAuth();
  const { startSignalExportPolling } = useDownloadPolling();
  const [organizations, setOrganizations] = useState([]);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  const [timePeriod, setTimePeriod] = useState('1');
  const [signalStartDate, setSignalStartDate] = useState('');
  const [signalEndDate, setSignalEndDate] = useState(getTodayDate());

  useEffect(() => {
    const start = new Date();
    start.setDate(start.getDate() - parseInt(timePeriod, 10));
    setSignalEndDate(getTodayDate());
    const y = start.getFullYear();
    const m = String(start.getMonth() + 1).padStart(2, '0');
    const d = String(start.getDate()).padStart(2, '0');
    setSignalStartDate(`${y}-${m}-${d}`);
  }, [timePeriod]);

  const [selectedOrganization, setSelectedOrganization] = useState('');

  const [useApiKeyRequest, setUseApiKeyRequest] = useState(false);
  const [apiKeyInputRequest, setApiKeyInputRequest] = useState('');

  const [targetFileSizeGb, setTargetFileSizeGb] = useState('100');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [executionId, setExecutionId] = useState(null);
  const [requestResult, setRequestResult] = useState(null);

  const [downloadHistory, setDownloadHistory] = useState([]);
  const [listOffset, setListOffset] = useState(0);
  const [loadingDownloadHistory, setLoadingDownloadHistory] = useState(false);

  const [downloadFilesModalOpen, setDownloadFilesModalOpen] = useState(false);
  const [downloadFilesModalRow, setDownloadFilesModalRow] = useState(null);
  const [downloadFilesList, setDownloadFilesList] = useState([]);
  const [downloadFilesLoading, setDownloadFilesLoading] = useState(false);
  const [downloadFilesError, setDownloadFilesError] = useState('');
  const [expandedDownloadHistoryId, setExpandedDownloadHistoryId] = useState(null);

  const openDownloadFilesModal = async (row) => {
    setDownloadFilesModalRow(row);
    setDownloadFilesModalOpen(true);
    setDownloadFilesList([]);
    setDownloadFilesError('');
    setDownloadFilesLoading(true);
    try {
      const res = await api.get('/api/v2/signal_files', {
        params: {
          execution_id: row.execution_id,
          organization: row.organization_name || '',
        },
      });
      setDownloadFilesList(sortDownloadFileObjects(res.data?.objects));
    } catch (e) {
      setDownloadFilesError(formatApiErrorDetail(e.response?.data?.detail) || e.message || 'Failed to list files');
    } finally {
      setDownloadFilesLoading(false);
    }
  };

  const closeDownloadFilesModal = () => {
    setDownloadFilesModalOpen(false);
    setDownloadFilesModalRow(null);
    setDownloadFilesList([]);
    setDownloadFilesError('');
  };

  useEffect(() => {
    const loadOrganizations = async () => {
      try {
        const orgs = await organizationsService.getOrganizations();
        setOrganizations(orgs);
        
        // Load selected organization from cookie, or default to first
        const savedSelected = organizationsService.getSelectedOrganization('name');
        if (savedSelected && orgs.some(o => o.name === savedSelected)) {
          setSelectedOrganization(savedSelected);
          organizationsService.setSelectedOrganization(savedSelected, 'name', orgs);
        } else if (orgs.length > 0) {
          const defaultOrg = orgs[0].name;
          setSelectedOrganization(defaultOrg);
          organizationsService.setSelectedOrganization(defaultOrg, 'name', orgs);
        }
      } catch (error) {
        console.error('Error loading organizations:', error);
      }
    };

    loadOrganizations();
  }, []);

  const loadDownloadHistoryRef = React.useRef(0);
  const loadDownloadHistory = useCallback(async () => {
    const generation = loadDownloadHistoryRef.current + 1;
    loadDownloadHistoryRef.current = generation;
    setLoadingDownloadHistory(true);
    try {
      const res = await api.get('/api/v2/signal_download_history', {
        params: { limit: HISTORY_FETCH_LIMIT },
      });
      if (loadDownloadHistoryRef.current !== generation) return;
      setDownloadHistory(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      if (loadDownloadHistoryRef.current !== generation) return;
      console.error('Download history error:', e);
      setDownloadHistory([]);
    } finally {
      if (loadDownloadHistoryRef.current === generation) {
        setLoadingDownloadHistory(false);
      }
    }
  }, []);

  useEffect(() => {
    loadDownloadHistory();
  }, [loadDownloadHistory]);

  const pagedDownloadHistory = downloadHistory.slice(listOffset, listOffset + LIST_PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(downloadHistory.length / LIST_PAGE_SIZE) || 1);
  const currentPage = Math.floor(listOffset / LIST_PAGE_SIZE) + 1;

  useEffect(() => {
    if (listOffset > 0 && listOffset >= downloadHistory.length) {
      setListOffset((prev) => Math.max(0, prev - LIST_PAGE_SIZE));
    }
  }, [downloadHistory.length, listOffset]);

  useEffect(() => {
    return subscribeDownloadHistoryRefresh((kind) => {
      if (kind === 'signals') {
        loadDownloadHistory();
      }
    });
  }, [loadDownloadHistory]);

  const handleSignalRequest = async (e) => {
    e.preventDefault();

    if (!selectedOrganization) {
      setErrorMessage('Please select an organization.');
      setShowErrorDialog(true);
      return;
    }

    if (useApiKeyRequest && !apiKeyInputRequest.trim()) {
      setErrorMessage('Please enter an API key.');
      setShowErrorDialog(true);
      return;
    }

    if (!signalStartDate || !signalEndDate) {
      setErrorMessage('Please set a date range (start and end).');
      setShowErrorDialog(true);
      return;
    }
    if (signalStartDate > signalEndDate) {
      setErrorMessage('Start date must be on or before end date.');
      setShowErrorDialog(true);
      return;
    }

    const startUtc = new Date(`${signalStartDate}T00:00:00`);
    const endUtc = new Date(`${signalEndDate}T00:00:00`);
    const inclusiveDays =
      Math.round((endUtc.getTime() - startUtc.getTime()) / (24 * 60 * 60 * 1000)) + 1;
    const basePath = '/api/v2/signal_request';
    const rangeLabel = `${signalStartDate} → ${signalEndDate}`;

    setIsSubmitting(true);
    setRequestResult(null);

    try {
      let headers = {};

      if (useApiKeyRequest) {
        headers.Authorization = `apikey ${apiKeyInputRequest.trim()}`;
      }

      const config = {
        params: { organization: selectedOrganization },
        ...(Object.keys(headers).length ? { headers } : {}),
      };

      const requestBody = {
        organization: selectedOrganization,
        signal_start_date: signalStartDate,
        signal_end_date: signalEndDate,
        target_file_size_gb: parseInt(targetFileSizeGb, 10),
      };

      const res = await api.post(basePath, requestBody, config);

      const data = res.data;
      setRequestResult(data);
      setExecutionId(data.execution_id);

      toast.success(`Signal request (${rangeLabel}, ${inclusiveDays}d) submitted! Execution ID: ${data.execution_id}`);
      loadDownloadHistory();
      startSignalExportPolling(data.execution_id, selectedOrganization);

      setIsSubmitting(false);
    } catch (error) {
      console.error('Signal request error:', error);
      const errorMsg =
        formatApiErrorDetail(error.response?.data?.detail) ||
        error.message ||
        'Failed to submit signal request. Please try again.';
      setErrorMessage(errorMsg);
      setShowErrorDialog(true);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <PageHeaderActions>
        {organizations.length === 0 ? (
          <FunctionalSelect
            label="Organization"
            value=""
            onValueChange={() => {}}
            placeholder="No organizations"
            className={HEADER_SELECT_CLASS}
            disabled
          />
        ) : (
          <FunctionalSelect
            label="Organization"
            value={organizationsService.getSelectedOptionValue(organizations, selectedOrganization, 'name')}
            onValueChange={(value) => {
              const parsed = organizationsService.parseOrganizationOptionValue(value);
              if (parsed) {
                setSelectedOrganization(parsed.name);
                organizationsService.setSelectedOrganization(parsed.name, 'name', [{ name: parsed.name, id: parsed.id }]);
              } else {
                setSelectedOrganization(value);
                organizationsService.setSelectedOrganization(value, 'name', organizations);
              }
            }}
            placeholder="Select organization"
            disabled={isSubmitting}
            className={HEADER_SELECT_CLASS}
          >
            {organizations.map((org) => (
              <SelectItem key={org.id} value={organizationsService.orgToOptionValue(org)}>
                {org.name}
              </SelectItem>
            ))}
          </FunctionalSelect>
        )}
      </PageHeaderActions>
      <Card>
        <CardContent className="space-y-4 pt-6">
          <form onSubmit={handleSignalRequest} className="space-y-4">
            <div className="space-y-2">
              {organizations.length === 0 ? (
                <p className="text-sm text-amber-600">
                  No organizations available. Please contact an administrator to be added to an organization.
                </p>
              ) : null}
            </div>

            <div className="space-y-2">
              <FunctionalSelect
                label="Target file size"
                id="targetFileSizeGb"
                value={targetFileSizeGb}
                onValueChange={setTargetFileSizeGb}
                placeholder="Select target file size"
                className="w-1/4"
                disabled={isSubmitting || organizations.length === 0}
              >
                {TARGET_FILE_SIZE_GB_OPTIONS.map((gb) => (
                  <SelectItem key={gb} value={gb}>
                    {gb} GB
                  </SelectItem>
                ))}
              </FunctionalSelect>
              <p className="text-xs text-gray-500">
                Merged downloads are split into multiple files when they exceed this size.
              </p>
            </div>

            <div className="space-y-2">
              <FunctionalSelect
                label="Time Period"
                id="signalTimePeriod"
                value={timePeriod}
                onValueChange={setTimePeriod}
                placeholder="Select time period"
                className="w-1/4"
                disabled={isSubmitting || organizations.length === 0}
              >
                <SelectItem value="1">Last day</SelectItem>
                <SelectItem value="2">Last 2 days</SelectItem>
                <SelectItem value="3">Last 3 days</SelectItem>
                <SelectItem value="7">Last week</SelectItem>
                <SelectItem value="14">Last 2 weeks</SelectItem>
                <SelectItem value="30">Last 1 month</SelectItem>
                <SelectItem value="90">Last 3 months</SelectItem>
                <SelectItem value="180">Last 6 months</SelectItem>
              </FunctionalSelect>
              {signalStartDate && signalEndDate && (() => {
                const start = new Date();
                start.setDate(start.getDate() - parseInt(timePeriod, 10));
                const expectedFrom = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`;
                const expectedTo = getTodayDate();
                const isCustomRange = signalStartDate !== expectedFrom || signalEndDate !== expectedTo;
                return isCustomRange ? <p className="text-xs text-gray-500">Using custom date range</p> : null;
              })()}
            </div>

            <div className="grid grid-cols-2 gap-2 w-full md:w-1/2">
              <div className="space-y-1">
                <Label htmlFor="signalExportStartDate">Start date</Label>
                <DateField
                  id="signalExportStartDate"
                  value={signalStartDate}
                  onChange={setSignalStartDate}
                  required
                  disabled={isSubmitting || organizations.length === 0}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="signalExportEndDate">End date</Label>
                <DateField
                  id="signalExportEndDate"
                  value={signalEndDate}
                  onChange={setSignalEndDate}
                  required
                  disabled={isSubmitting || organizations.length === 0}
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="useApiKeyRequest"
                  checked={useApiKeyRequest}
                  onChange={(e) => {
                    setUseApiKeyRequest(e.target.checked);
                    if (!e.target.checked) {
                      setApiKeyInputRequest('');
                    }
                  }}
                  disabled={isSubmitting}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <Label htmlFor="useApiKeyRequest" className="cursor-pointer">
                  Use API Key
                </Label>
              </div>
            </div>

            {useApiKeyRequest && (
              <div className="space-y-2">
                <Label htmlFor="apiKeyInputRequest">API Key</Label>
                <Input
                  id="apiKeyInputRequest"
                  type="password"
                  autoComplete="off"
                  value={apiKeyInputRequest}
                  onChange={(e) => setApiKeyInputRequest(e.target.value)}
                  placeholder="Enter API key"
                  disabled={isSubmitting}
                />
              </div>
            )}

            {executionId && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-md">
                <p className="text-sm font-medium text-green-800">
                  Request submitted successfully!
                </p>
                <p className="text-sm text-green-700 mt-1">
                  Execution ID: <code className="bg-green-100 px-2 py-1 rounded">{executionId}</code>
                </p>
                <p className="text-xs text-green-600 mt-2">
                  Track status and download below in Download History when the download completes.
                </p>
              </div>
            )}

            <Button
              type="submit"
              variant="outline"
              disabled={isSubmitting || !selectedOrganization || (useApiKeyRequest && !apiKeyInputRequest.trim())}
            >
              {isSubmitting ? 'Submitting...' : 'Submit request'}
            </Button>
          </form>

          {requestResult && useApiKeyRequest && apiKeyInputRequest.trim() && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Query:</h3>
              <div className="bg-gray-50 border border-gray-200 rounded-md p-4 overflow-auto">
                <pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono">
                  {(() => {
                    const baseUrl = window.location.origin;
                    const apiKeyValue = '<API_KEY_TOKEN>';
                    const basePath = '/api/v2/signal_request';
                    const requestBody = JSON.stringify({
                      signal_start_date: signalStartDate || '<start_date>',
                      signal_end_date: signalEndDate || '<end_date>',
                      target_file_size_gb: parseInt(targetFileSizeGb, 10) || 100,
                    });
                    const isLocalhost = baseUrl.includes('localhost') || baseUrl.includes('127.0.0.1');
                    const isHttps = baseUrl.startsWith('https://');
                    const kFlag = isLocalhost && isHttps ? ' -k' : '';

                    return `curl -X POST "${baseUrl}${basePath}"${kFlag} \\\n  -H "Authorization: apikey ${apiKeyValue}" \\\n  -H "Content-Type: application/json" \\\n  -d '${requestBody}'`;
                  })()}
                </pre>
              </div>
            </div>
          )}

          {requestResult && useApiKeyRequest && apiKeyInputRequest.trim() && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Result:</h3>
              <div className="bg-gray-50 border border-gray-200 rounded-md p-4 overflow-auto">
                <pre className="text-sm text-gray-800 whitespace-pre-wrap">
                  {JSON.stringify(requestResult, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Download History</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {loadingDownloadHistory ? (
            <p className="text-sm text-gray-500">Loading download history...</p>
          ) : downloadHistory.length === 0 ? (
            <p className="text-sm text-gray-500">No signal downloads yet.</p>
          ) : (
            <>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm border border-gray-200 rounded-md">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-2 text-left font-medium text-gray-700 border-b w-9" aria-label="Expand" />
                    <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">Request time</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">Organization</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700 border-b whitespace-nowrap">
                      Start date
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700 border-b whitespace-nowrap">
                      End date
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">Status</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700 border-b min-w-[6rem]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedDownloadHistory.map((row) => {
                    const rowKey = row.id;
                    return (
                      <React.Fragment key={rowKey}>
                        <tr
                          className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                          onClick={() =>
                            setExpandedDownloadHistoryId((prev) => (prev === rowKey ? null : rowKey))
                          }
                        >
                          <td className="px-2 py-2 text-gray-500" onClick={(e) => e.stopPropagation()}>
                            <button
                              type="button"
                              onClick={() =>
                                setExpandedDownloadHistoryId((prev) => (prev === rowKey ? null : rowKey))
                              }
                              className="p-0.5 rounded hover:bg-gray-200"
                              aria-expanded={expandedDownloadHistoryId === rowKey}
                              aria-label={expandedDownloadHistoryId === rowKey ? 'Collapse row' : 'Expand row'}
                            >
                              {expandedDownloadHistoryId === rowKey ? (
                                <ChevronDown size={18} />
                              ) : (
                                <ChevronRight size={18} />
                              )}
                            </button>
                          </td>
                          <td className="px-3 py-2 text-gray-800 whitespace-nowrap">
                            {row.request_time ? new Date(row.request_time).toLocaleString() : '—'}
                          </td>
                          <td className="px-3 py-2 text-gray-800">{row.organization_name || '—'}</td>
                          <td className="px-3 py-2 text-gray-800 whitespace-nowrap">
                            {formatSignalDownloadDateCell(row.start_date)}
                          </td>
                          <td className="px-3 py-2 text-gray-800 whitespace-nowrap">
                            {formatSignalDownloadDateCell(row.end_date)}
                          </td>
                          <td className="px-3 py-2">
                            <span
                              className={`px-2 py-0.5 rounded text-xs font-medium ${downloadHistoryStatusBadgeClass(row.processing_status)}`}
                            >
                              {row.processing_status || '—'}
                            </span>
                          </td>
                          <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                            {canDownloadSignalFromHistory(row.processing_status) && row.execution_id ? (
                              <button
                                type="button"
                                onClick={() => openDownloadFilesModal(row)}
                                className="inline-flex items-center justify-center rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 border border-[#0062FB] bg-[#0062FB] text-white hover:bg-[#0052E0] hover:border-[#0052E0] h-10 px-4 py-2 whitespace-nowrap"
                              >
                                List Files
                              </button>
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </td>
                        </tr>
                        {expandedDownloadHistoryId === rowKey && (
                          <tr className="border-b border-gray-100 bg-gray-50">
                            <td colSpan={7} className="px-3 py-2">
                              <div className="text-xs text-gray-600 space-y-1">
                                {row.execution_id && (
                                  <div>
                                    <span className="font-medium text-gray-700">Execution ID:</span>{' '}
                                    <code
                                      className="break-all bg-gray-100 px-2 py-1 rounded"
                                      title={row.execution_id}
                                    >
                                      {row.execution_id}
                                    </code>
                                  </div>
                                )}
                                {currentUser?.is_admin && row.s3_url && (
                                  <div>
                                    <span className="font-medium text-gray-700">S3 URL:</span>{' '}
                                    <code
                                      className="break-all bg-gray-100 px-2 py-1 rounded"
                                      title={row.s3_url}
                                    >
                                      {row.s3_url}
                                    </code>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between gap-3 pt-3 text-sm text-gray-600">
              <span>
                {downloadHistory.length.toLocaleString()} download
                {downloadHistory.length === 1 ? '' : 's'}
                {` · page ${currentPage} of ${totalPages}`}
              </span>
              <ListPagination
                currentPage={currentPage}
                totalPages={totalPages}
                disabled={loadingDownloadHistory}
                onPageChange={(page) => {
                  setListOffset((page - 1) * LIST_PAGE_SIZE);
                  setExpandedDownloadHistoryId(null);
                }}
              />
            </div>
            </>
          )}
        </CardContent>
      </Card>

      <Modal
        isOpen={downloadFilesModalOpen}
        onClose={closeDownloadFilesModal}
        className="w-full max-w-4xl max-h-[85vh] flex flex-col"
      >
            <CardHeader className="pb-2 border-b pr-10">
              <CardTitle className="text-lg text-gray-900">Signal Files</CardTitle>
            </CardHeader>
            <CardContent className="overflow-y-auto flex-1 min-h-0 pt-4 space-y-3">
              {downloadFilesModalRow && (
                <p className="text-sm text-gray-600">
                  <span className="font-medium text-gray-800">Execution ID:</span>{' '}
                  <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded break-all">
                    {downloadFilesModalRow.execution_id}
                  </code>
                </p>
              )}
              {downloadFilesLoading && <p className="text-sm text-gray-500">Loading objects…</p>}
              {downloadFilesError && (
                <p className="text-sm text-red-700 whitespace-pre-line">{downloadFilesError}</p>
              )}
              {!downloadFilesLoading && !downloadFilesError && downloadFilesList.length === 0 && (
                <p className="text-sm text-gray-500">No <code className="text-xs">.csv.gz</code> files found under this prefix.</p>
              )}
              {!downloadFilesLoading && downloadFilesList.length > 0 && (
                <div className="overflow-x-auto border border-gray-200 rounded-md">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-700 border-b whitespace-nowrap min-w-[22rem]">Filename</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700 border-b whitespace-nowrap">Size</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700 border-b whitespace-nowrap">Modified</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700 border-b min-w-[5rem]"> </th>
                      </tr>
                    </thead>
                    <tbody>
                      {downloadFilesList.map((obj) => (
                        <tr key={obj.key} className="border-b border-gray-100">
                          <td className="px-3 py-2 text-gray-800 font-mono text-xs whitespace-nowrap min-w-[22rem]">
                            {s3ObjectFilename(obj.key)}
                          </td>
                          <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{formatFileSize(obj.size)}</td>
                          <td className="px-3 py-2 text-gray-700 whitespace-nowrap text-xs">
                            {obj.last_modified
                              ? new Date(obj.last_modified).toLocaleString()
                              : '—'}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <a
                              href={
                                downloadFilesModalRow
                                  ? buildFileDownloadHref('/api/v2/requested_signals_download', {
                                      id: downloadFilesModalRow.execution_id,
                                      organization: downloadFilesModalRow.organization_name || '',
                                      filename: s3ObjectFilename(obj.key),
                                    })
                                  : '#'
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              className={DOWNLOAD_BTN_CLASS}
                            >
                              Download
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
      </Modal>

      <ErrorDialog
        isOpen={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        title="Error"
        message={errorMessage}
      />
    </div>
  );
};

export default Signals;
